// src/features/ucits-etf/components/Screener.tsx
//
// In-memory UCITS-ETF screener. Fires the POST /ucits-etfs/screen mutation
// against the Polars speed layer and renders the matches in a dense,
// tabular-nums table. The control panel drives four backend filter
// dimensions:
//   - max_expense_ratio  (percent slider, e.g. 0.20 → "0.20%")
//   - min_aum            (millions slider → sent as raw dollars)
//   - min_liquidity      (thousands-of-shares slider → sent as raw volume)
//   - sectors[]          (chip multi-select; AND semantics on the backend)
//   - is_ucits_only      (toggle)
//
// An initial screen runs on mount so the grid is never empty. The engine's
// self-reported latency + total match count are surfaced in the footer to
// make the "microsecond" story tangible.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    Database,
    Loader2,
    RotateCcw,
    SlidersHorizontal,
    Zap,
} from 'lucide-react';

import { formatCompactMoney } from '../../../shared/utils/currency';
import {
    useDividendPolicies,
    useEtfScreener,
    useEtfSectors,
} from '../api/etfQueries';
import {
    buildPageList,
    buildScreenerPayload,
    DEFAULT_FILTERS,
    PAGE_SIZE,
    type DividendPolicy,
    type ScreenerFilters,
} from '../lib/screener';
import { etfInfoPath } from '../routes';
import { useScreenerStore } from '../store/useScreenerStore';
import type { EtfDetail } from '../types';

type SortKey = 'expense_ratio' | 'aum' | 'avg_volume';
type SortDir = 'asc' | 'desc';

interface SortableHeaderProps {
    label: string;
    columnKey: SortKey;
    sortKey: SortKey | null;
    sortDir: SortDir;
    onSort: (key: SortKey) => void;
}

// A right-aligned, clickable numeric column header. Shows a neutral
// up/down glyph until its column is the active sort, then the concrete
// direction arrow. Pure presentational — all sort state lives in Screener.
const SortableHeader = ({
    label,
    columnKey,
    sortKey,
    sortDir,
    onSort,
}: SortableHeaderProps) => {
    const active = sortKey === columnKey;
    return (
        <th
            className="px-6 py-5 text-right"
            aria-sort={
                active
                    ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                    : 'none'
            }
        >
            <button
                type="button"
                onClick={() => onSort(columnKey)}
                aria-label={`Sort by ${label}`}
                className={`inline-flex items-center gap-1 uppercase tracking-widest rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                    active
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'hover:text-slate-600 dark:hover:text-slate-300'
                }`}
            >
                <span>{label}</span>
                {active ? (
                    sortDir === 'asc' ? (
                        <ArrowUp size={12} />
                    ) : (
                        <ArrowDown size={12} />
                    )
                ) : (
                    <ArrowUpDown size={12} className="opacity-40" />
                )}
            </button>
        </th>
    );
};

export const Screener = () => {
    // Persisted filter state (localStorage via Zustand) — restored on return
    // visits. Individual primitive selectors avoid subscribing to the whole
    // object (no new-object-every-render infinite loop).
    const maxExpense = useScreenerStore((s) => s.maxExpense);
    const minAumMil = useScreenerStore((s) => s.minAumMil);
    const minVolK = useScreenerStore((s) => s.minVolK);
    const ucitsOnly = useScreenerStore((s) => s.ucitsOnly);
    const selectedSectors = useScreenerStore((s) => s.selectedSectors);
    const dividendPolicy = useScreenerStore((s) => s.dividendPolicy);
    const setFilters = useScreenerStore((s) => s.setFilters);
    const resetStore = useScreenerStore((s) => s.reset);

    // Ephemeral view state — column sort is intentionally NOT persisted.
    // `null` key preserves the backend's native order (AUM desc), so an
    // unsorted grid is identical to before.
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    // 1-based current page. Server-side pagination: each page is one backend
    // request for a PAGE_SIZE window, so memory/DOM stay flat regardless of
    // how many thousand funds match.
    const [page, setPage] = useState<number>(1);

    // Scroll the grid back to the top on every page change.
    const scrollRef = useRef<HTMLDivElement>(null);

    const { data: sectors } = useEtfSectors();
    // Fall back to the two known literals so the control still renders even
    // if the vocabulary request is in-flight or fails.
    const { data: policies = ['Accumulating', 'Distributing'] } =
        useDividendPolicies();
    const {
        mutate: screenEtfs,
        data: response,
        isPending,
        isError,
    } = useEtfScreener();

    // Current UI filter snapshot — the single object passed to the pure
    // payload builder for both the Update button and the mount screen.
    const filters = useMemo<ScreenerFilters>(
        () => ({
            maxExpense,
            minAumMil,
            minVolK,
            ucitsOnly,
            selectedSectors,
            dividendPolicy,
        }),
        [
            maxExpense,
            minAumMil,
            minVolK,
            ucitsOnly,
            selectedSectors,
            dividendPolicy,
        ],
    );

    // Screen a specific page with the CURRENT filters. `overrideFilters` lets
    // Reset screen the defaults without waiting for the store write to flush.
    const screenPage = (targetPage: number, overrideFilters?: ScreenerFilters) => {
        const safePage = Math.max(1, targetPage);
        const offset = (safePage - 1) * PAGE_SIZE;
        setPage(safePage);
        screenEtfs(
            buildScreenerPayload(overrideFilters ?? filters, PAGE_SIZE, offset),
        );
        // Jump back to the first row of the new page.
        scrollRef.current?.scrollTo({ top: 0 });
    };

    // "Update Matrix" applies the current filters from page 1.
    const runScreen = () => screenPage(1);

    // Restore every filter to its default and immediately re-screen page 1.
    const resetFilters = () => {
        resetStore();
        setSortKey(null);
        setSortDir('desc');
        screenPage(1, DEFAULT_FILTERS);
    };

    // Run an initial screen on mount so the grid renders immediately. persist
    // hydrates synchronously, so `filters` already reflects the restored
    // choices on first render.
    useEffect(() => {
        runScreen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reconcile persisted sector chips against the live universe: if the
    // sector taxonomy changed since the choice was saved, silently drop any
    // stale sector so a phantom AND-filter can't zero out every result.
    useEffect(() => {
        if (!sectors) return;
        const valid = selectedSectors.filter((s) => sectors.includes(s));
        if (valid.length !== selectedSectors.length) {
            setFilters({ selectedSectors: valid });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectors]);

    const toggleSector = (sector: string) => {
        const next = selectedSectors.includes(sector)
            ? selectedSectors.filter((s) => s !== sector)
            : [...selectedSectors, sector];
        setFilters({ selectedSectors: next });
    };

    // Clicking an active column toggles direction; a new column starts desc
    // (the intuitive "biggest first" for money/liquidity metrics).
    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const results = response?.results;

    // Purely client-side sort over the returned rows. Nulls always sink to
    // the bottom regardless of direction so "—" cells never crowd the top.
    const sortedResults = useMemo<EtfDetail[] | undefined>(() => {
        if (!results) return results;
        if (!sortKey) return results;
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...results].sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return (av - bv) * dir;
        });
    }, [results, sortKey, sortDir]);

    // Pagination derived from the last response's total. `page` may briefly
    // exceed totalPages if filters shrink the set, but the next response
    // recomputes everything, so the controls always converge.
    const totalMatches = response?.total_matches ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalMatches / PAGE_SIZE));
    const pageItems = buildPageList(page, totalPages);
    const rowsOnPage = results?.length ?? 0;
    const rangeStart = totalMatches === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + rowsOnPage;

    return (
        <div className="surface-card !p-0 overflow-hidden flex flex-col xl:flex-row">
            {/* Control panel ------------------------------------------------ */}
            <div className="w-full xl:w-80 shrink-0 p-8 flex flex-col gap-8
                            border-b xl:border-b-0 xl:border-r border-slate-200 dark:border-slate-700
                            bg-slate-50 dark:bg-slate-900/40">
                <div className="flex items-center gap-3 text-slate-900 dark:text-slate-100">
                    <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                        <SlidersHorizontal size={20} />
                    </div>
                    <h3 className="font-extrabold text-lg tracking-tight">
                        Engine Filters
                    </h3>
                </div>

                {/* Max expense ratio */}
                <div className="flex flex-col gap-3">
                    <label className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        <span>Max Expense</span>
                        <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">
                            {maxExpense.toFixed(2)}%
                        </span>
                    </label>
                    <input
                        type="range"
                        min="0.05"
                        max="1.5"
                        step="0.05"
                        value={maxExpense}
                        onChange={(e) =>
                            setFilters({
                                maxExpense: parseFloat(e.target.value),
                            })
                        }
                        aria-label="Maximum expense ratio (percent)"
                        className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-emerald-600 bg-slate-200 dark:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    />
                </div>

                {/* Min AUM */}
                <div className="flex flex-col gap-3">
                    <label className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        <span>Min AUM</span>
                        <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">
                            ${minAumMil.toLocaleString()}M
                        </span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="2000"
                        step="10"
                        value={minAumMil}
                        onChange={(e) =>
                            setFilters({
                                minAumMil: parseInt(e.target.value, 10),
                            })
                        }
                        aria-label="Minimum assets under management (millions)"
                        className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-emerald-600 bg-slate-200 dark:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    />
                </div>

                {/* Min liquidity */}
                <div className="flex flex-col gap-3">
                    <label className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        <span>Min Avg Volume</span>
                        <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">
                            {minVolK > 0 ? `${minVolK.toLocaleString()}k` : 'Any'}
                        </span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="1000"
                        step="10"
                        value={minVolK}
                        onChange={(e) =>
                            setFilters({
                                minVolK: parseInt(e.target.value, 10),
                            })
                        }
                        aria-label="Minimum average daily volume (thousands of shares)"
                        className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-emerald-600 bg-slate-200 dark:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    />
                </div>

                {/* UCITS-only toggle */}
                <button
                    type="button"
                    role="switch"
                    aria-checked={ucitsOnly}
                    onClick={() => setFilters({ ucitsOnly: !ucitsOnly })}
                    className="flex items-center justify-between gap-3 text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        UCITS Only
                    </span>
                    <span
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            ucitsOnly
                                ? 'bg-emerald-600'
                                : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                ucitsOnly ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                    </span>
                </button>

                {/* Dividend policy — Accumulating vs Distributing. "All"
                    clears the filter so the backend skips the equality scan. */}
                <div className="flex flex-col gap-3">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        Dividend Policy
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            aria-pressed={dividendPolicy === ''}
                            onClick={() => setFilters({ dividendPolicy: '' })}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                                dividendPolicy === ''
                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                            }`}
                        >
                            All
                        </button>
                        {policies.map((policy) => {
                            const active = dividendPolicy === policy;
                            return (
                                <button
                                    key={policy}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() =>
                                        setFilters({
                                            dividendPolicy:
                                                policy as DividendPolicy,
                                        })
                                    }
                                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                                        active
                                            ? 'bg-emerald-600 text-white border-emerald-600'
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                                    }`}
                                >
                                    {policy}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Sector chips */}
                {sectors && sectors.length > 0 && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                Sectors
                            </span>
                            {selectedSectors.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFilters({ selectedSectors: [] })
                                    }
                                    className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                >
                                    Clear sectors
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                            {/* "All" convenience — empty selection means the
                                backend applies no sector filter at all. */}
                            <button
                                type="button"
                                aria-pressed={selectedSectors.length === 0}
                                onClick={() =>
                                    setFilters({ selectedSectors: [] })
                                }
                                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                                    selectedSectors.length === 0
                                        ? 'bg-emerald-600 text-white border-emerald-600'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                                }`}
                            >
                                All
                            </button>
                            {sectors.map((sector) => {
                                const active = selectedSectors.includes(sector);
                                return (
                                    <button
                                        key={sector}
                                        type="button"
                                        aria-pressed={active}
                                        onClick={() => toggleSector(sector)}
                                        className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                                            active
                                                ? 'bg-emerald-600 text-white border-emerald-600'
                                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                                        }`}
                                    >
                                        {sector}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="mt-2 flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={runScreen}
                        disabled={isPending}
                        className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold shadow-md transition-all disabled:opacity-70
                                   bg-slate-900 dark:bg-emerald-600 text-white hover:bg-slate-800 dark:hover:bg-emerald-500"
                    >
                        {isPending ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <span>Update Matrix</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={resetFilters}
                        disabled={isPending}
                        className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-70
                                   bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300
                                   border border-slate-200 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-500"
                    >
                        <RotateCcw size={15} />
                        <span>Reset Filters</span>
                    </button>
                </div>
            </div>

            {/* Results table ------------------------------------------------ */}
            <div className="flex-1 min-w-0 flex flex-col">
                <div
                    ref={scrollRef}
                    aria-busy={isPending}
                    className={`flex-1 overflow-auto min-h-[400px] transition-opacity ${
                        isPending && results ? 'opacity-60' : 'opacity-100'
                    }`}
                >
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="sticky top-0 z-10 bg-white dark:bg-slate-800/80 backdrop-blur
                                          text-slate-400 dark:text-slate-500 uppercase text-[10px] font-extrabold tracking-widest
                                          border-b border-slate-100 dark:border-slate-700">
                            <tr>
                                <th className="px-6 py-5">Symbol</th>
                                <th className="px-6 py-5">Fund Name</th>
                                <th className="px-6 py-5">Sector</th>
                                <SortableHeader
                                    label="Expense"
                                    columnKey="expense_ratio"
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                    onSort={toggleSort}
                                />
                                <SortableHeader
                                    label="AUM"
                                    columnKey="aum"
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                    onSort={toggleSort}
                                />
                                <SortableHeader
                                    label="Avg Vol"
                                    columnKey="avg_volume"
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                    onSort={toggleSort}
                                />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                            {isError ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-24 text-center text-rose-500 font-medium"
                                    >
                                        The screener engine hit an error. Please
                                        try again.
                                    </td>
                                </tr>
                            ) : isPending && !results ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-24 text-center text-slate-400 dark:text-slate-500"
                                    >
                                        <Loader2
                                            className="mx-auto animate-spin"
                                            size={28}
                                        />
                                    </td>
                                </tr>
                            ) : !results ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-24 text-center text-slate-400 dark:text-slate-500"
                                    >
                                        <Database
                                            className="mx-auto mb-3 opacity-30"
                                            size={32}
                                        />
                                    </td>
                                </tr>
                            ) : results.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-24 text-center text-slate-400 dark:text-slate-500 font-medium"
                                    >
                                        No UCITS ETFs match your exact criteria.
                                    </td>
                                </tr>
                            ) : (
                                (sortedResults ?? results).map((etf) => (
                                    <tr
                                        key={etf.symbol}
                                        className="group transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
                                    >
                                        <td className="px-6 py-4 font-bold">
                                            <Link
                                                to={etfInfoPath(etf.symbol)}
                                                title={`Open ${etf.symbol} deep-dive`}
                                                className="text-slate-900 dark:text-slate-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 hover:underline transition-colors"
                                            >
                                                {etf.symbol}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-slate-300 font-medium truncate max-w-[220px]">
                                            {etf.name}
                                        </td>
                                        <td className="px-6 py-4 text-slate-500 dark:text-slate-400">
                                            {etf.sector ?? '—'}
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono tabular-nums font-semibold text-slate-700 dark:text-slate-200">
                                            {etf.expense_ratio != null ? (
                                                <span
                                                    className={`px-2 py-1 rounded ${
                                                        etf.expense_ratio <= 0.15
                                                            ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                                            : 'bg-slate-100 dark:bg-slate-700/50'
                                                    }`}
                                                >
                                                    {etf.expense_ratio.toFixed(2)}%
                                                </span>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono tabular-nums font-semibold text-slate-700 dark:text-slate-200">
                                            {formatCompactMoney(
                                                etf.aum ?? undefined,
                                                {
                                                    explicitCurrency:
                                                        etf.currency,
                                                    symbol: etf.symbol,
                                                    exchange: etf.exchange,
                                                },
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono tabular-nums text-slate-500 dark:text-slate-400">
                                            {etf.avg_volume != null
                                                ? etf.avg_volume.toLocaleString(
                                                      undefined,
                                                      { maximumFractionDigits: 0 },
                                                  )
                                                : '—'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination bar. */}
                {response && totalPages > 1 && (
                    <nav
                        aria-label="Screener pagination"
                        className="flex items-center justify-center gap-1 px-6 py-3
                                   border-t border-slate-100 dark:border-slate-700"
                    >
                        <button
                            type="button"
                            aria-label="Previous page"
                            disabled={isPending || page <= 1}
                            onClick={() => screenPage(page - 1)}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors
                                       border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300
                                       hover:border-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed
                                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        {pageItems.map((item, idx) =>
                            item === 'ellipsis' ? (
                                <span
                                    key={`gap-${idx}`}
                                    className="px-1.5 text-slate-400 dark:text-slate-500 select-none"
                                >
                                    …
                                </span>
                            ) : (
                                <button
                                    key={item}
                                    type="button"
                                    aria-label={`Page ${item}`}
                                    aria-current={item === page ? 'page' : undefined}
                                    disabled={isPending}
                                    onClick={() => screenPage(item)}
                                    className={`inline-flex items-center justify-center h-8 min-w-8 px-2 rounded-lg border text-[12px] font-bold tabular-nums transition-colors
                                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed ${
                                                    item === page
                                                        ? 'bg-emerald-600 text-white border-emerald-600'
                                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400 disabled:opacity-60'
                                                }`}
                                >
                                    {item}
                                </button>
                            ),
                        )}

                        <button
                            type="button"
                            aria-label="Next page"
                            disabled={isPending || page >= totalPages}
                            onClick={() => screenPage(page + 1)}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors
                                       border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300
                                       hover:border-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed
                                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </nav>
                )}

                {/* Latency + match-count footer. */}
                {response && (
                    <div className="flex items-center justify-between gap-3 px-6 py-3
                                    border-t border-slate-100 dark:border-slate-700
                                    text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                        <span className="tabular-nums">
                            {totalMatches.toLocaleString()} match
                            {totalMatches === 1 ? '' : 'es'}
                            {totalMatches > 0
                                ? ` · showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()}`
                                : ''}
                            {totalPages > 1
                                ? ` · page ${page.toLocaleString()} of ${totalPages.toLocaleString()}`
                                : ''}
                        </span>
                        <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                            <Zap size={12} />
                            <span className="tabular-nums">
                                {response.execution_time_ms.toFixed(2)} ms
                            </span>
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Screener;
